'use client';

import {
  amplitudeSendEvent,
  GTM_NOTSET_STRING,
  getGtmAttr,
  getScreenName,
  pushDataLayer,
} from '@musinsa/frontend-utils';
import { Button, Padding, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { useQuery } from '@tanstack/react-query';
import { type FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { pathsMusinsa } from '../../common';
import { useNavigate } from '../../common/hooks/useNavigate';
import { curatorQueries } from '../../query-factory';
import { MyShopProfileEditBottomSheetButton } from './MyShopProfileEditBottomSheetButton';
import { ShopLinkCopyButton } from './ShopLinkCopyButton';

interface BrandCollaborationCouponButtonProps {
  snsTitle: string;
}

const BrandCollaborationCouponButton: FC<BrandCollaborationCouponButtonProps> = ({ snsTitle }) => {
  const { service } = useDeviceInfo();
  const { navigate } = useNavigate();

  const { data, isSuccess } = useQuery({
    ...curatorQueries.coupon.count(snsTitle),
    enabled: service === SERVICE.MUSINSA,
  });

  const handleClick = () => {
    if (service !== SERVICE.MUSINSA) return;

    amplitudeSendEvent({
      action_name: 'click',
      button_name: 'collaboration_coupon_list',
      component_name: 'button',
      event_name: 'click_button',
      from: getScreenName(),
      name: '나의 브랜드 협업 쿠폰',
      section_name: 'curator_collaboration',
    });
    pushDataLayer('click_button', {
      section_name: 'curator_collaboration',
      section_index: '2',
      button_name: 'collaboration_coupon_list',
      index: GTM_NOTSET_STRING,
    });
    navigate(pathsMusinsa.myShopCoupon({ shopSlug: snsTitle, query: { direct: true } }));
  };

  if (!isSuccess) return null;
  if (data.totalCount === 0) return null;

  return (
    <div
      {...getGtmAttr(['impression_button'], {
        button_name: 'collaboration_coupon_list',
        index: GTM_NOTSET_STRING,
        section_name: 'curator_collaboration',
        section_index: '1',
      })}
    >
      <Typography size="13" weight="medium" asChild>
        <Button size="32" variant="secondary" className="w-full" onClick={handleClick}>
          나의 브랜드 협업 쿠폰
        </Button>
      </Typography>
    </div>
  );
};

interface Props {
  className?: string;
}

export const MyShopProfileButtons = ({ className }: Props) => {
  const [open, setOpen] = useState(false);

  const userInfoQuery = useQuery({
    ...curatorQueries.profile.userInfo,
  });

  return (
    <Padding x="4" y="3" className={twMerge('w-full flex flex-col gap-[4px]', className)}>
      <div className="flex gap-[4px]">
        <MyShopProfileEditBottomSheetButton
          className="flex-1"
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
        />
        <ShopLinkCopyButton className="flex-1" />
      </div>
      {userInfoQuery.data?.snsTitle && (
        <BrandCollaborationCouponButton snsTitle={userInfoQuery.data.snsTitle} />
      )}
    </Padding>
  );
};
